<script lang="ts">
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { GiftIdea } from '@consumer/types/serializers'
import type { SpecOptions } from '@consumer/composables/spec'
import type { MaybeRefOrGetter } from 'vue'
import { encodeGift } from '@consumer/composables/giftTemplate'
import { customizer } from '@consumer/api'
import { cloneDeep, isBlank } from '@shared/object'

export interface CustomizerLinkOptions {
  deliveryDate?: string | null
  // Names the occasion on the checkout delivery summary ("Send on Christmas
  // (Dec 25)") while the prefilled deliveryDate is in effect.
  eventName?: string | null
  // Carried through to the checkout wizard's Contact step (see checkoutGift),
  // so a logged-out user arriving from an event reminder link is prefilled.
  gifterEmail?: string | null
  // Carried through to the checkout wizard's Delivery step (see checkoutGift),
  // prefilling the recipient's email/phone from the gifter's saved contact.
  recipientContactId?: number | null
}

function customizerUrlParams (
  idea?: GiftIdea | GiftTemplate,
  specOptionsRef?: MaybeRefOrGetter<SpecOptions>,
  options?: CustomizerLinkOptions,
) {
  const urlParams = new URLSearchParams('')
  const specOptions = toValue(specOptionsRef)
  if (specOptions?.purchaseMethod) urlParams.set('purchase_method', specOptions.purchaseMethod)
  if (specOptions?.spec) urlParams.set('spec', specOptions.spec)
  if (idea) urlParams.set('gift', encodeGift(idea))
  if (options?.deliveryDate) urlParams.set('delivery_date', options.deliveryDate)
  if (options?.deliveryDate && options?.eventName) urlParams.set('event_name', options.eventName)
  if (options?.gifterEmail) urlParams.set('gifter_email', options.gifterEmail)
  if (options?.recipientContactId) urlParams.set('recipient_contact_id', String(options.recipientContactId))
  return urlParams
}

// TODO: Unify links to new customizer with the approach in CustomizerModal.
export function customizerPathFromIdea (
  idea?: GiftIdea | GiftTemplate,
  specOptionsRef?: MaybeRefOrGetter<SpecOptions>,
  options?: CustomizerLinkOptions,
) {
  return customizer.show.path({ query: customizerUrlParams(idea, specOptionsRef, options) })
}
</script>

<script lang="ts" setup>import { toRef as _toRef, computed as _computed } from 'vue';

import { openCustomizerModal } from '@consumer/services/modals'
import { useSpec } from '@consumer/composables/spec'
import { useSharedData } from '@consumer/composables/sharedData'
import { useScreen } from '@consumer/helpers/screen'

withDefaults(defineProps<{
  idea?: GiftIdea
  name?: string
  onCardClick?: (() => void)
  hideTools?: boolean
}>(), {  })

const specOptions = useSpec()

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser');
const gift = _computed<GiftTemplate>(() => __props.idea || {})

function handleCardClick () {
  // Call parent's callback if provided (e.g., for tracking)
  __props.onCardClick?.()

  // Open the customizer modal
  openCustomizerModal({ gift: gift.value, specOptions: toValue(specOptions) })
}

const customizeUrl = _computed(() => customizerPathFromIdea({ ...cloneDeep(__props.idea), giftTags: [] }, specOptions))

const alt = _computed(() => {
  if (isBlank(__props.idea)) return 'Create My Own'

  const tagline = __props.idea.giftlyMetadata?.tagline
  const places = __props.idea.places?.map(p => p?.name).join(', ')
  return `Send a gift $${__props.idea.amount} for ${tagline} at ${places}`
})

const __$temp_2 = (useScreen()),
  isDesktop = _toRef(__$temp_2, 'isDesktop');
const canUseTools = !__props.hideTools && currentUser.value?.canEditGiftIdeas

const GiftIdeaTools = !import.meta.env.SSR && canUseTools
  ? defineAsyncComponent(() => import('@consumer/admin/GiftIdeaTools.vue'))
  : 'template'
</script>

<template>
  <a
    :href="customizeUrl"
    :name="name"
    :alt="alt"
    class="relative clickable group"
    rel="nofollow"
    @click.exact.prevent="handleCardClick"
  >
    <GiftlyV3Image
      :gift="gift"
      :createMyOwn="!idea"
    >
      <div class="absolute inset-0 bg-primary-base opacity-20 hidden group-hover:block"/>
    </GiftlyV3Image>

    <GiftIdeaTools
      v-if="canUseTools && isDesktop && idea?.id"
      :idea="idea"
      :gift="gift"
    />

    <FavoriteButton
      v-if="idea && !hideTools"
      :favoritable="idea"
      :label="false"
      :params="Object.fromEntries(customizerUrlParams(gift, specOptions))"
      class="absolute -top-3 -right-3 lg:opacity-0 group-hover:opacity-100"
    />
  </a>
</template>
